<template>
  <b-modal
    v-model="isOpen"
    size="xl"
    centered
    no-close-on-backdrop
    no-enforce-focus
    hide-header-close
    dialog-class="furnace-details-modal-dialog"
    content-class="furnace-details-modal"
  >
    <template #modal-header>
      <div class="d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
          <div class="furnace-status-icon mr-1" :style="{ backgroundColor: statusBgColor, color: (charge && charge.statusColor) || '#ff9f43' }">
            <feather-icon icon="ThermometerIcon" size="24" />
          </div>
          <div>
            <div class="d-flex align-items-center">
              <h4 class="modal-title-custom mb-0 mr-50">{{ (furnace && furnace.furnaceName) || 'Forno' }}</h4>
              <span :style="{ backgroundColor: (charge && charge.statusColor) || '#ff9f43', color: '#fff' }" class="badge font-weight-bold">
                {{ (charge && charge.status) || 'Em Carga' }}
              </span>
            </div>
            <span class="modal-subtitle-custom">
              Código da Carga: <strong>{{ (charge && charge.chargeCode) || '-' }}</strong>
            </span>
          </div>
        </div>
        <button type="button" class="close-btn-custom" @click="closeModal">
          <feather-icon icon="XIcon" size="20" />
        </button>
      </div>
    </template>

    <div class="details-modal-body">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" label="Carregando..." class="mb-1" />
        <p class="text-muted">Carregando detalhes da carga...</p>
      </div>

      <div v-else-if="charge">
        <!-- Métricas Principais da Carga -->
        <div class="row mb-1">
          <div class="col-6 col-md-3 mb-1 mb-md-0">
            <div class="metric-card">
              <span class="metric-title">Cestos Carregados</span>
              <span class="metric-val text-primary">{{ charge.totalBaskets || 0 }} / {{ (furnace && furnace.capacity) || 12 }}</span>
            </div>
          </div>
          <div class="col-6 col-md-3 mb-1 mb-md-0">
            <div class="metric-card">
              <span class="metric-title">Total de Barras</span>
              <span class="metric-val text-warning">{{ charge.totalBars || 0 }} un</span>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="metric-card">
              <span class="metric-title">Início da Carga</span>
              <span class="metric-val text-muted" style="font-size: 13px;">{{ formatDateTime(charge.startedAt) }}</span>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="metric-card highlight-timer">
              <span class="metric-title">Tempo Decorrido</span>
              <span class="metric-val text-success">{{ formatDuration(liveSeconds) }}</span>
            </div>
          </div>
        </div>

        <!-- Observação se houver -->
        <div v-if="charge.observation" class="observation-box p-1 mb-1">
          <span class="font-weight-600 text-muted">Observação:</span>
          <span class="ml-25 text-dark">{{ charge.observation }}</span>
        </div>

        <!-- Tabela de Cestos da Carga -->
        <div class="baskets-table-wrapper mb-1">
          <h5 class="table-title mb-50">
            <feather-icon icon="BoxIcon" size="16" class="mr-50 text-warning" />
            Cestos da Fornada ({{ charge.totalBaskets || groupedChargeBaskets.length }})
          </h5>

          <div class="table-responsive-custom">
            <table class="custom-table w-100">
              <thead>
                <tr>
                  <th style="width: 40px;">#</th>
                  <th>Cesto</th>
                  <th>OP Vinculada</th>
                  <th>Material / Liga</th>
                  <th class="text-center">Têmpera</th>
                  <th class="text-center">Qtd. Barras</th>
                  <th class="text-right">Tamanho</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(group, idx) in groupedChargeBaskets" :key="group.key">
                  <td class="font-weight-bold text-muted">{{ idx + 1 }}</td>
                  <td>
                    <strong class="text-warning">{{ group.basketCode }}</strong>
                    <span v-if="group.items.length > 1" class="badge badge-light-primary ml-25 font-size-xs">
                      {{ group.items.length }} OPs
                    </span>
                  </td>
                  <td>
                    <div v-for="(item, iIdx) in group.items" :key="item.id" :class="{ 'mt-25 pt-25 border-top-dashed': iIdx > 0 }">
                      <span v-if="item.orderNumber" class="badge-op-table">
                        OP {{ item.orderNumber }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </div>
                  </td>
                  <td>
                    <div v-for="(item, iIdx) in group.items" :key="item.id" :class="{ 'mt-25 pt-25 border-top-dashed': iIdx > 0 }">
                      <span class="material-name-cell" :title="item.materialName">
                        {{ item.materialName || 'Material Padrão' }}
                      </span>
                    </div>
                  </td>
                  <td class="text-center">
                    <div v-for="(item, iIdx) in group.items" :key="item.id" :class="{ 'mt-25 pt-25 border-top-dashed': iIdx > 0 }">
                      <span class="badge badge-light-secondary font-weight-600">
                        {{ item.temper || '-' }}
                      </span>
                    </div>
                  </td>
                  <td class="text-center">
                    <div v-if="group.items.length === 1">
                      <strong>{{ group.items[0].barQuantity }}</strong> un
                    </div>
                    <div v-else>
                      <strong>{{ group.totalBarQuantity }}</strong> un
                      <div class="text-muted font-size-xs">
                        ({{ group.items.map(i => i.barQuantity).join(' + ') }})
                      </div>
                    </div>
                  </td>
                  <td class="text-right font-weight-500">
                    <div v-for="(item, iIdx) in group.items" :key="item.id" :class="{ 'mt-25 pt-25 border-top-dashed': iIdx > 0 }">
                      {{ formatBarSize(item.barSizeMm) }}
                    </div>
                  </td>
                </tr>
                <tr v-if="groupedChargeBaskets.length === 0">
                  <td colspan="7" class="text-center py-2 text-muted">
                    Nenhum cesto registrado nesta carga.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Ações Operacionais de Transição de Estado -->
        <div class="action-buttons-box p-1 d-flex flex-wrap align-items-center justify-content-between">
          <div class="d-flex align-items-center mb-50 mb-md-0">
            <!-- Botão de Editar Carga (Cestos) -->
            <button
              class="btn-action-edit mr-1"
              :disabled="charge.status === 'Finalizado'"
              @click="openEditCharge"
              title="Adicionar ou remover cestos desta fornada"
            >
              <feather-icon icon="Edit3Icon" class="mr-50" />
              <span>Editar Carga</span>
            </button>

            <!-- Botão de Parada (quando Operando ou Aquecendo) -->
            <button
              v-if="charge.status !== 'Finalizado' && !isChargeStopped"
              class="btn-action-stop mr-1"
              :disabled="saving"
              @click="openStopReasonModal"
              title="Registrar parada no forno"
            >
              <feather-icon icon="PauseCircleIcon" class="mr-50" />
              <span>Parada</span>
            </button>

            <!-- Botão de Retomar Fornada (quando Parado) -->
            <button
              v-else-if="isChargeStopped"
              class="btn-action-resume mr-1"
              :disabled="saving"
              @click="resumeCharge"
              title="Retomar operação do forno"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              <feather-icon v-else icon="PlayCircleIcon" class="mr-50" />
              <span>Retomar Fornada</span>
            </button>
          </div>

          <!-- Ação Principal de Finalização (lado direito) -->
          <div>
            <!-- Botão de Finalizar Fornada / Descarregar -->
            <button
              v-if="charge.status !== 'Finalizado'"
              class="btn-action-finish"
              :disabled="saving"
              @click="confirmFinalizeCharge"
            >
              <b-spinner v-if="saving" small class="mr-50" />
              <feather-icon v-else icon="CheckSquareIcon" class="mr-50" />
              <span>Finalizar Fornada / Descarregar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Sub-Modal: Motivo de Parada do Forno -->
    <b-modal
      v-model="showStopModal"
      title="Registrar Parada do Forno"
      centered
      hide-footer
      no-enforce-focus
      dialog-class="furnace-stop-modal-dialog"
      content-class="furnace-stop-modal"
    >
      <div class="p-1">
        <div class="d-flex align-items-center mb-1">
          <div class="furnace-status-icon mr-1" style="background-color: #fce8e8; color: #ea5455; width: 40px; height: 40px; border-radius: 8px; display: flex; align-items: center; justify-content: center;">
            <feather-icon icon="PauseCircleIcon" size="22" />
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold">{{ (furnace && furnace.furnaceName) || 'Forno' }}</h5>
            <small class="text-muted">Selecione o motivo da parada para a carga atual</small>
          </div>
        </div>

        <b-form-group label="Motivo da Parada *" label-class="font-weight-bold font-size-sm">
          <div v-if="loadingStopStatuses" class="d-flex align-items-center py-50">
            <b-spinner small variant="primary" class="mr-50" />
            <small class="text-muted">Carregando motivos de parada...</small>
          </div>
          <b-form-select
            v-else
            v-model="selectedStopStatusId"
            :options="stopStatusOptions"
            class="form-control"
          />
        </b-form-group>

        <b-form-group label="Observação (Opcional)" label-class="font-weight-bold font-size-sm">
          <b-form-textarea
            v-model="stopObservation"
            rows="2"
            placeholder="Descreva detalhes sobre a parada, se necessário..."
            max-rows="4"
          />
        </b-form-group>

        <div class="d-flex justify-content-end align-items-center mt-2 pt-1 border-top">
          <button type="button" class="btn btn-outline-secondary mr-1" @click="showStopModal = false">
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="!selectedStopStatusId || saving"
            @click="confirmStopCharge"
          >
            <b-spinner v-if="saving" small class="mr-50" />
            <feather-icon v-else icon="CheckIcon" class="mr-50" />
            <span>Confirmar Parada</span>
          </button>
        </div>
      </div>
    </b-modal>

    <template #modal-footer>
      <div class="d-flex align-items-center justify-content-end w-100">
        <button class="btn-operator-close" @click="closeModal">
          Fechar
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment';
import { BModal, BSpinner, BFormGroup, BFormSelect, BFormTextarea } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { getFurnaceChargeDetails, updateFurnaceChargeStatus } from '../extrusionApi';

export default {
  name: 'FurnaceChargeDetailsModal',
  components: {
    BModal,
    BSpinner,
    BFormGroup,
    BFormSelect,
    BFormTextarea,
    ToastificationContent
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    furnace: {
      type: Object,
      default: () => ({ furnaceId: 1, furnaceName: 'Forno 1' })
    },
    chargeData: {
      type: Object,
      default: null
    },
    siteId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      charge: null,
      loading: false,
      saving: false,
      liveSeconds: 0,
      timerInterval: null,
      showStopModal: false,
      loadingStopStatuses: false,
      stopStatusList: [],
      selectedStopStatusId: null,
      stopObservation: ''
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.open;
      },
      set(val) {
        this.$emit('update:open', val);
      }
    },
    statusBgColor() {
      if (this.charge?.status === 'Em operação') return '#f0effc';
      if (this.charge?.status === 'Finalizado') return '#e8fadf';
      if (this.isChargeStopped) return '#fce8e8';
      return '#fff3e8';
    },
    isChargeStopped() {
      const s = this.charge?.status;
      if (!s) return false;
      return s !== 'Em operação' && s !== 'Aquecendo' && s !== 'Finalizado' && s !== 'Disponível';
    },
    groupedChargeBaskets() {
      const rawList = this.charge?.baskets || [];
      if (!Array.isArray(rawList)) return [];
      const map = new Map();
      for (const b of rawList) {
        const rawCode = b.basketCode ? String(b.basketCode).trim() : '';
        const key = rawCode ? rawCode.toUpperCase() : `ID_${b.id}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            basketCode: rawCode || (b.id ? `C${b.id}` : '-'),
            items: [],
            totalBarQuantity: 0
          });
        }
        const group = map.get(key);
        group.items.push(b);
        group.totalBarQuantity += Number(b.barQuantity || 0);
      }
      return Array.from(map.values());
    },
    stopStatusOptions() {
      return (this.stopStatusList || []).map(s => ({
        value: s.id || s.value,
        text: s.text || s.name || `Status ${s.id}`
      }));
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(val) {
        if (val) {
          this.initData();
        } else {
          this.stopTimer();
        }
      }
    }
  },
  beforeDestroy() {
    this.stopTimer();
  },
  methods: {
    async initData() {
      if (this.chargeData?.id) {
        this.charge = { ...this.chargeData };
        this.startTimer();
        await this.fetchDetails(this.chargeData.id);
      }
    },
    async fetchDetails(chargeId) {
      this.loading = true;
      try {
        const res = await getFurnaceChargeDetails(chargeId);
        if (res.data?.data) {
          this.charge = res.data.data;
          this.liveSeconds = this.charge.elapsedTimeSeconds || 0;
        }
      } catch (err) {
        console.error('Erro ao buscar detalhes da carga:', err);
      } finally {
        this.loading = false;
      }
    },
    startTimer() {
      this.stopTimer();
      if (!this.charge?.startedAt) return;
      const startMs = moment(this.charge.startedAt).valueOf();
      this.liveSeconds = Math.max(0, Math.floor((Date.now() - startMs) / 1000));

      this.timerInterval = setInterval(() => {
        if (this.charge?.status !== 'Finalizado') {
          this.liveSeconds = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
        }
      }, 1000);
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      return moment(dateStr).format('DD/MM/YYYY HH:mm');
    },
    formatDuration(totalSecs) {
      if (!totalSecs || totalSecs < 0) return '00:00:00';
      const hrs = Math.floor(totalSecs / 3600);
      const mins = Math.floor((totalSecs % 3600) / 60);
      const secs = totalSecs % 60;
      return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },
    formatBarSize(size) {
      if (!size) return '-';
      let num = Number(size);
      if (isNaN(num) || num <= 0) return '-';
      if (num <= 50) num *= 1000;
      return `${num.toFixed(0)} mm`;
    },
    closeModal() {
      this.isOpen = false;
      this.$emit('close');
    },
    async changeStatus(newStatus) {
      if (!this.charge?.id) return;
      this.saving = true;
      try {
        const payload = {
          chargeId: this.charge.id,
          newStatus
        };
        const res = await updateFurnaceChargeStatus(payload, this.siteId);
        if (res.data?.success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Status Alterado',
              icon: 'CheckIcon',
              text: `Status da carga alterado para "${newStatus}".`,
              variant: 'success'
            }
          });
          this.charge = res.data.data;
          this.$emit('charge-updated', this.charge);
          this.closeModal();
        }
      } catch (err) {
        console.error('Erro ao alterar status da carga:', err);
        const msg = err.response?.data?.message || 'Erro ao alterar status da carga.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            icon: 'AlertTriangleIcon',
            text: msg,
            variant: 'danger'
          }
        });
      } finally {
        this.saving = false;
      }
    },
    async confirmFinalizeCharge() {
      if (this.charge?.status === 'Finalizado') {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Fornada Finalizada',
            icon: 'AlertCircleIcon',
            text: 'Esta fornada já se encontra finalizada.',
            variant: 'warning'
          }
        });
        return;
      }

      const res = await this.$swal({
        title: 'Finalizar Fornada?',
        text: `Tem certeza que deseja finalizar e descarregar a fornada ${this.charge.chargeCode}? Todos os cestos serão concluídos e o ${this.furnace.furnaceName} ficará disponível para novas cargas.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, Finalizar e Descarregar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      });

      if (!res || (!res.isConfirmed && res.value !== true)) return;

      this.saving = true;
      try {
        const payload = {
          chargeId: this.charge.id,
          newStatus: 'Finalizado'
        };
        const res = await updateFurnaceChargeStatus(payload, this.siteId);
        if (res.data?.success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Fornada Finalizada',
              icon: 'CheckIcon',
              text: `Fornada finalizada com sucesso! O ${this.furnace.furnaceName} agora está Disponível.`,
              variant: 'success'
            }
          });
          this.$emit('charge-finalized', res.data.data);
          this.closeModal();
        }
      } catch (err) {
        console.error('Erro ao finalizar fornada:', err);
        const msg = err.response?.data?.message || 'Erro ao finalizar fornada.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Finalizar',
            icon: 'AlertCircleIcon',
            text: msg,
            variant: 'danger'
          }
        });
      } finally {
        this.saving = false;
      }
    },
    openEditCharge() {
      const furnaceId = (this.furnace && (this.furnace.furnaceId || this.furnace.id)) || 1;
      const prefix = this.$route.params.prefix || 'sfc';
      this.closeModal();
      this.$router.push({
        name: 'operator-extrusion-furnace-assembly',
        params: { prefix, furnaceId }
      });
    },
    async openStopReasonModal() {
      this.stopObservation = '';
      this.selectedStopStatusId = null;
      this.showStopModal = true;
      await this.fetchStopStatuses();
    },
    async fetchStopStatuses() {
      this.loadingStopStatuses = true;
      try {
        const furnaceId = (this.furnace && (this.furnace.furnaceId || this.furnace.id)) || (this.charge && this.charge.furnaceId) || '';
        const res = await this.$http2.get(`/api/mysfc/operation-progress/context?siteId=${this.siteId}&resourceId=${furnaceId}`);
        if (res.data?.data?.status) {
          const allStatuses = res.data.data.status;
          const stopList = allStatuses.filter(s =>
            (s.status_classification_id === 1 && (s.name !== 'Setup' || s.id === 2 || s.value === 2)) ||
            s.status_classification_id === 2 ||
            s.status_classification_id === 3
          );
          this.stopStatusList = stopList;
          if (stopList.length > 0) {
            this.selectedStopStatusId = stopList[0].id || stopList[0].value;
          }
        }
      } catch (err) {
        console.error('Erro ao carregar motivos de parada:', err);
      } finally {
        this.loadingStopStatuses = false;
      }
    },
    async confirmStopCharge() {
      if (!this.selectedStopStatusId || !this.charge?.id) return;
      this.saving = true;
      try {
        const payload = {
          chargeId: this.charge.id,
          newStatus: (this.stopStatusList.find(s => (s.id || s.value) === this.selectedStopStatusId) || {}).name || 'Parado',
          statusId: this.selectedStopStatusId,
          observation: this.stopObservation || undefined
        };
        const res = await updateFurnaceChargeStatus(payload, this.siteId);
        if (res.data?.success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Parada Registrada',
              icon: 'CheckIcon',
              text: 'Parada do forno registrada com sucesso.',
              variant: 'success'
            }
          });
          this.charge = res.data.data;
          this.showStopModal = false;
          this.$emit('charge-updated', this.charge);
        }
      } catch (err) {
        console.error('Erro ao registrar parada:', err);
        const msg = err.response?.data?.message || 'Erro ao registrar parada do forno.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Parar Forno',
            icon: 'AlertCircleIcon',
            text: msg,
            variant: 'danger'
          }
        });
      } finally {
        this.saving = false;
      }
    },
    async resumeCharge() {
      const res = await this.$swal({
        title: 'Retomar Fornada?',
        text: `Deseja retornar o ${this.furnace.furnaceName || 'Forno'} para o status "Em operação"?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, Retomar Fornada',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1'
        },
        buttonsStyling: false
      });

      if (!res || (!res.isConfirmed && res.value !== true)) return;

      this.saving = true;
      try {
        const payload = {
          chargeId: this.charge.id,
          newStatus: 'Em operação'
        };
        const updateRes = await updateFurnaceChargeStatus(payload, this.siteId);
        if (updateRes.data?.success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Fornada Retomada',
              icon: 'CheckIcon',
              text: 'O forno voltou para o status Em operação.',
              variant: 'success'
            }
          });
          this.charge = updateRes.data.data;
          this.$emit('charge-updated', this.charge);
        }
      } catch (err) {
        console.error('Erro ao retomar fornada:', err);
        const msg = err.response?.data?.message || 'Erro ao retomar fornada.';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao Retomar Forno',
            icon: 'AlertCircleIcon',
            text: msg,
            variant: 'danger'
          }
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss">
.furnace-details-modal-dialog {
  max-width: 1140px !important;
  width: 95vw;
  margin-left: auto !important;
  margin-right: auto !important;

  @media (min-width: 1400px) {
    max-width: 1200px !important;
  }
}

.furnace-details-modal {
  border-radius: 12px !important;
  overflow: hidden;

  .modal-header {
    background: #f8f7fa;
    border-bottom: 1px solid #ebe9f1;
    padding: 16px 24px;

    .furnace-status-icon {
      width: 44px;
      height: 44px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-title-custom {
      font-family: 'Raleway', sans-serif;
      font-weight: 700;
      color: #4c4541;
      font-size: 18px;
    }

    .modal-subtitle-custom {
      font-size: 12px;
      color: #998f8a;
    }

    .close-btn-custom {
      background: transparent;
      border: none;
      color: #998f8a;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s;

      &:hover {
        background: #ebe9f1;
        color: #4c4541;
      }
    }
  }

  .details-modal-body {
    padding: 16px 24px;

    .metric-card {
      background: #faf9f8;
      border: 1px solid #e2ddd8;
      border-radius: 8px;
      padding: 10px 14px;
      display: flex;
      flex-direction: column;

      .metric-title {
        font-size: 11px;
        font-weight: 600;
        color: #998f8a;
        margin-bottom: 4px;
      }

      .metric-val {
        font-size: 16px;
        font-weight: 700;
      }

      &.highlight-timer {
        background: #f0fff4;
        border-color: #b9ebd0;
      }
    }

    .observation-box {
      background: #faf9f8;
      border: 1px solid #e2ddd8;
      border-radius: 6px;
      font-size: 12px;
    }

    .baskets-table-wrapper {
      .table-title {
        font-family: 'Raleway', sans-serif;
        font-weight: 700;
        font-size: 14px;
        color: #4c4541;
      }

      .table-responsive-custom {
        max-height: 380px;
        overflow-y: auto;
        border: 1px solid #ebe9f1;
        border-radius: 6px;

        .custom-table {
          border-collapse: collapse;
          font-size: 12px;

          thead th {
            background: #f8f7fa;
            color: #6e6b7b;
            font-weight: 600;
            padding: 8px 12px;
            border-bottom: 1px solid #ebe9f1;
            position: sticky;
            top: 0;
            z-index: 1;
          }

          tbody td {
            padding: 8px 12px;
            border-bottom: 1px solid #f1f0f4;
            color: #5e5873;
          }

          tbody tr:hover {
            background: #faf9f8;
          }

          .border-top-dashed {
            border-top: 1px dashed #ebe9f1;
          }
        }
      }

      .badge-op-table {
        font-size: 10px;
        font-weight: 600;
        background: #f0effc;
        color: #7367f0;
        padding: 2px 6px;
        border-radius: 4px;
      }

      .material-name-cell {
        display: inline-block;
        max-width: 320px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .action-buttons-box {
      background: #faf9f8;
      border: 1px solid #e2ddd8;
      border-radius: 8px;

      .btn-action-edit {
        height: 38px;
        padding: 0 16px;
        background: #f3f2f7;
        border: 1px solid #d8d6de;
        border-radius: 6px;
        color: #5e5873;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #ebe9f1;
          color: #7367f0;
          border-color: #7367f0;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .btn-action-stop {
        height: 38px;
        padding: 0 16px;
        background: #fff;
        border: 1px solid #ea5455;
        border-radius: 6px;
        color: #ea5455;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #fce8e8;
          color: #d32f2f;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .btn-action-resume {
        height: 38px;
        padding: 0 16px;
        background: #28c76f;
        border: 1px solid #28c76f;
        border-radius: 6px;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #22a95e;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .btn-action-primary {
        height: 38px;
        padding: 0 16px;
        background: #7367f0;
        border: none;
        border-radius: 6px;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #5e50ee;
        }
      }

      .btn-action-secondary {
        height: 38px;
        padding: 0 16px;
        background: #fff;
        border: 1px solid #ff9f43;
        border-radius: 6px;
        color: #ff9f43;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #fff3e8;
        }
      }

      .btn-action-finish {
        height: 38px;
        padding: 0 18px;
        background: #28c76f;
        border: none;
        border-radius: 6px;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #22a95e;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }

  .modal-footer {
    background: #f8f7fa;
    border-top: 1px solid #ebe9f1;
    padding: 10px 24px;

    .btn-operator-close {
      height: 36px;
      padding: 0 20px;
      background: #ebe9f1;
      border: none;
      border-radius: 6px;
      font-weight: 600;
      font-size: 13px;
      color: #6e6b7b;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #d8d6de;
      }
    }
  }
}
</style>
